// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
import { LogLevel, type Configuration } from '@/types/msal-shim'
import type { AuthContextResponse } from '@/types/auth_context';
import { getAuthContextState } from '@/uptk/core/api/auth_context_store';

// Detect browser to determine whether or not to use Cookies
const ua = typeof window !== 'undefined' ? window.navigator.userAgent : '';
const msie = ua.indexOf("MSIE ");
const msie11 = ua.indexOf("Trident/");
const msedge = ua.indexOf("Edge/");
const firefox = ua.indexOf("Firefox");
const isIE = msie > 0 || msie11 > 0;
const isEdge = msedge > 0;
const isFirefox = firefox > 0; // Only needed if you need to support the redirect flow in Firefox incognito

const commonCacheConfig = {
    cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: isIE || isEdge || isFirefox,
}

const commonSystemConfig = {
    allowNativeBroker: false, // Disables WAM Broker
    loggerOptions: {
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
            if (containsPii) {
                return;
            }
            switch (level) {
                case LogLevel.Error:
                    console.error(message);
                    return;
                case LogLevel.Warning:
                    console.warn(message);
                    return;
                default:
                    break;
            }
        }
    }
}

const commonGraphMe = {
    endpoint: "https://graph.microsoft.com/v1.0/me", scopes: ["User.Read"],
}

const commonGraphPhoto = {
    endpoint: "https://graph.microsoft.com/v1.0/me/photo/$value", scopes: ["User.Read", "User.Read.All"],
}

const commonGraphMessages = {
    endpoint: "https://graph.microsoft.com/v1.0/me/messages", scopes: ["Mail.Read"],
}

const commonGraphContacts = {
    endpoint: 'https://graph.microsoft.com/v1.0/me/contacts',
    scopes: ['Contacts.Read'],
}

const commonArmTenants = {
    endpoint: "https://management.azure.com/tenants?api-version=2020-01-01",
    scopes: ["https://management.azure.com/user_impersonation"],
}

// --- Interfaces ---

export interface AppSite {
    type?: 'protected' | 'preview' | 'public';
    groups?: string[];
    sites?: Record<string, AppSite>;
}

export interface AppMenu {
    id: string;
    groups: string[];
    route: string;
    label: string;
    selectable: boolean;
    selected: boolean;
    protected: boolean;
    requiresModule?: string;
}

export interface GeodeMsalConfig extends Configuration {
    appFeatures: string[];
    auth: Configuration['auth'] & {
        id: string;
        tenantId: string;
        logo: string;
        application: string;
        company: string;
        manifest: string;
        start_url: string;
        frontURL: string;
        backendURL: string;
        checkIslocalHost: boolean;
        sites: Record<string, AppSite>;
        menus: {
            users: AppMenu[];
            admins: AppMenu[];
        };
        workspaceIds: Array<{
            id: string;
            name: string;
            groups: string[];
            selectable: boolean;
            selected: boolean;
        }>;
        userGroup: { id: string; role: string; selectable: boolean };
        adminGroup: { id: string; role: string; selectable: boolean };
        previewGroup: { id: string; role: string; selectable: boolean };
        developersGroup: { id: string; role: string; selectable: boolean };
        userRole: { id: string; role: string; selectable: boolean };
        adminRole: { id: string; role: string; selectable: boolean };
        previewRole: { id: string; role: string; selectable: boolean };
        developersRole: { id: string; role: string; selectable: boolean };
        secretExpirationDate: Date;
        ADGroups: Array<{ name: string; uuid: string; description?: string }>;
        protectedResources: {
            graphMe: { endpoint: string; scopes: string[] };
            graphPhoto: { endpoint: string; scopes: string[] };
            graphMessages: { endpoint: string; scopes: string[] };
            graphContacts: { endpoint: string; scopes: string[] };
            scopeRoot: string;
            scopes: string[];
            api: { endpoint: string };
            armTenants: { endpoint: string; scopes: string[] };
        };
    };
}

// --- Constants ---

// Legacy Azure AD wiring is DEAD (Clerk-only auth since UPT-436); ids neutralized 26.08 — never point at a real corporate tenant.
const TENANTID = "00000000-0000-0000-0000-000000000000"
const CLIENTID = "00000000-0000-0000-0000-000000000000" // legacy placeholder, unused
const GEODE = "geode" // Application geode api

const AUTHORITYONLEGACYAD = `https://login.microsoftonline.com/${TENANTID}` // tenant ID (Azure AD), legacy tenant

const ADGROUPSUPTKCH = [
   {
        name: "geode-developers", uuid: "75eb2c49-ac62-4b31-92cf-05601c058f64", description: "Géode Developer Group"
    }
];

const ACL_ALL = ["geode-developers"];

const FRONTURL: Record<string, string> = {
    PROD: 'https://geode.uptk.ch',
    QUAL: 'https://geode-qual.uptk.ch',
    QUAL2: 'https://geode-qual2.uptk.ch',
    LOCAL: 'http://localhost:3000',
    DEV: 'https://gray-rock-04864f903.7.azurestaticapps.net',
};

const BACKENDURL: Record<string, string> = {
    PROD: 'https://geode-rest.uptk.ch',
    QUAL: 'https://geode-rest-qual.uptk.ch',
    QUAL2: 'https://geode-rest-qual2.uptk.ch',
    // VITE_API_URL (.env, default 8080) lets a second local backend be targeted.
    LOCAL: (import.meta.env?.VITE_API_URL as string | undefined) || 'http://localhost:8080',
    // DEV SWA uses the SWA linked-backend proxy: all /api/* calls are relative
    // so the SWA gateway forwards them to the ACA. Empty string = relative URLs.
    DEV: '',
};

export type AuthBaseConfigKey =
    | 'id'
    | 'frontURL'
    | 'backendURL'
    | 'redirectUri'
    | 'postLogoutRedirectUri'
    | 'clientId'
    | 'authority'
    | 'groups'
    | 'navigateToLoginRequestUrl'
    | 'secretExpirationDate'
    | 'scopeRoot'
    | 'scopes'
    | 'protectedResources'

const getAuthBaseConfig = (key: AuthBaseConfigKey, env?: string): any => {
    switch (key) {
        case "id":
            return env
        case "frontURL":
        case "redirectUri":
        case "postLogoutRedirectUri":
            return env ? FRONTURL[env.toUpperCase()] : undefined
        case "backendURL":
            return env ? BACKENDURL[env.toUpperCase()] : undefined
        case "clientId":
            return CLIENTID
        case "authority":
            return AUTHORITYONLEGACYAD
        case "groups":
            return ADGROUPSUPTKCH
        case "navigateToLoginRequestUrl":
            return false
        case "secretExpirationDate":
            return new Date("2024-08-10")
        case "scopeRoot":
            return "api://geode"
        case "scopes":
            return [
                "api://geode/access_as_admin",
                "api://geode/access_as_user",
                'User.Read',
                'User.ReadBasic.All'
            ]
        default:
            return null;
    }
}

function getEnvHeaders(env: string) {
    return {
        logo: "geode",
        application: "Geode",
        company: "UPTK",
        manifest: "manifest-UPTK.json",
        start_url: "index-UPTK.html",
    }
}

const getProtectedResources = (scopeRoot: string, backendURL: string) => {
    return {
        graphMe: commonGraphMe,
        graphPhoto: commonGraphPhoto,
        graphMessages: commonGraphMessages,
        graphContacts: commonGraphContacts,
        scopeRoot: scopeRoot,
        scopes: [
            `${scopeRoot}/access_as_admin`,
            `${scopeRoot}/access_as_user`,
            'User.Read',
            'User.ReadBasic.All'
        ],
        api: {
            endpoint: backendURL + "/api",
        },
        armTenants: commonArmTenants
    };
}

const commonSites: Record<string, AppSite> = {
    portal: {
        sites: {
            finance: {
                type: 'protected', groups: ACL_ALL,
                sites: {
                    budget: {
                        type: 'protected', groups: ACL_ALL,
                        sites: {
                            budget: { type: 'protected', groups: ACL_ALL },
                            reclassify: { type: 'protected', groups: ACL_ALL },
                            budgetrequestextra: { type: 'preview', groups: ACL_ALL }
                        }
                    }
                }
            }
        }
    },
    home: {
        sites: {
            finance: {
                type: 'protected', groups: ACL_ALL,
                sites: {
                    budget: {
                        type: 'protected', groups: ACL_ALL,
                        sites: {
                            budget: { type: 'protected', groups: ACL_ALL },
                            budgetconsumption: { type: 'protected', groups: ACL_ALL },
                            budgetline: { type: 'protected', groups: ACL_ALL },
                            reclassify: { type: 'protected', groups: ACL_ALL },
                            budgetrequestextra: { type: 'preview', groups: ACL_ALL },
                        }
                    },
                    invoice: {
                        type: 'protected', groups: ACL_ALL,
                        sites: {
                            order: { type: 'preview', groups: ACL_ALL },
                            invoice: { type: 'protected', groups: ACL_ALL },
                            invoiceline: { type: 'protected', groups: ACL_ALL },
                        }
                    },
                    psf: {
                        type: 'protected', groups: ACL_ALL,
                        sites: {
                            psf: { type: 'preview', groups: ACL_ALL },
                            psfline: { type: 'preview', groups: ACL_ALL }
                        }
                    }
                }
            },
            project: {
                type: 'protected', groups: ACL_ALL,
                sites: {
                    project: { type: 'protected', groups: ACL_ALL },
                    budgetrequest: { type: 'preview', groups: ACL_ALL },
                    order: { type: 'preview', groups: ACL_ALL },
                    budgetrequestextra: { type: 'preview', groups: ACL_ALL }
                }
            },
            parameter: {
                type: 'protected', groups: ACL_ALL,
                sites: {
                    team: { type: 'protected', groups: ACL_ALL },
                    fileloading: { type: 'protected', groups: ACL_ALL },
                    fileexport: { type: 'protected', groups: ACL_ALL },
                    glaccount: { type: 'protected', groups: ACL_ALL },
                    ca: { type: 'protected', groups: ACL_ALL },
                    auditlog: { type: 'protected', groups: ACL_ALL },
                    parameter: { type: 'protected', groups: ACL_ALL }
                }
            },
            admin: { type: 'protected', groups: ACL_ALL },
            about: { type: 'public' },
        },
        type: 'public'
    }
};

const commonMenus = {
    users: [
        {
            id: 'PORTAL', groups: ACL_ALL, route: '/portal', label: 'menu.portal',
            selectable: true, selected: true, protected: false
        },  {
            id: 'FINANCE', groups: ACL_ALL, route: '/portal/finance', label: 'menu.finance',
            selectable: true, selected: true, protected: true
        }, {
            id: 'PROJECT', groups: ACL_ALL, route: '/portal/project', label: 'menu.project',
            selectable: true, selected: true, protected: true
        }, {
            id: 'PARAMETER', groups: ACL_ALL, route: '/portal/parameter', label: 'menu.parameter',
            selectable: true, selected: true, protected: true
        }, {
            id: 'MARKETPLACE', groups: ACL_ALL, route: '/portal/marketplace', label: 'menu.marketplace',
            selectable: true, selected: true, protected: true
        }, {
            id: 'ADMINPORTAL', groups: ACL_ALL, route: '/adminportal', label: 'menu.adminportal',
            selectable: true, selected: true, protected: false
        }
    ],
    admins: [
        {
            id: 'PORTAL', groups: ACL_ALL, route: '/portal', label: 'menu.portal',
            selectable: true, selected: true, protected: false
        }, {
            id: 'FINANCE', groups: ACL_ALL, route: '/finance', label: 'menu.finance',
            selectable: true, selected: true, protected: true
        }, {
            id: 'PROJECT', groups: ACL_ALL, route: '/project', label: 'menu.project',
            selectable: true, selected: true, protected: true
        }, {
            id: 'BUSINESS', groups: ACL_ALL, route: '/business', label: 'menu.business',
            selectable: true, selected: true, protected: true
        }, {
            id: 'APPLICATION', groups: ACL_ALL, route: '/application', label: 'menu.application',
            selectable: true, selected: true, protected: true
        }, {
            id: 'VERUM', groups: ACL_ALL, route: '/infrastructure', label: 'menu.verum',
            selectable: true, selected: true, protected: true
        }, {
            id: 'PARAMETER', groups: ACL_ALL, route: '/parameter', label: 'menu.parameter',
            selectable: true, selected: true, protected: true
        }, {
            id: 'MARKETPLACE', groups: ACL_ALL, route: '/portal/marketplace', label: 'menu.marketplace',
            selectable: true, selected: true, protected: true
        }, {
            id: 'ADMINPORTAL', groups: ACL_ALL, route: '/adminportal', label: 'menu.adminportal',
            selectable: true, selected: true, protected: false
        }
    ]
};

const msalConfig: Record<string, GeodeMsalConfig> = {};

msalConfig["prod"] = {
    appFeatures: ['appPMO', 'appDFHB', 'appHEADER_ShowEnv', 'appHEADER_ShowworkspaceId', 'appHEADER_ShowworkspaceId_Auto', 'appPORTAL_ShowGraphics'],
    auth: {
        id: 'PROD',
        tenantId: "",
        ...getEnvHeaders('prod'),
        frontURL: FRONTURL.PROD,
        redirectUri: FRONTURL.PROD,
        postLogoutRedirectUri: FRONTURL.PROD,
        backendURL: BACKENDURL.PROD,
        checkIslocalHost: false,
        clientId: CLIENTID,
        authority: AUTHORITYONLEGACYAD,
        sites: commonSites,
        menus: commonMenus,
        workspaceIds: [{ id: "default", name: "UPTK", groups: ACL_ALL, selectable: true, selected: true }],
        userGroup: { id: "DG_PF01-TEAMS-CLOUDM365E5STD", role: "users", selectable: true },
        adminGroup: { id: "OG_480", role: "admins", selectable: true },
        previewGroup: { id: "DG_PF01-GEODE-PREVIEW", role: "preview", selectable: false },
        developersGroup: { id: "geode-developers", role: "developers", selectable: false },
        userRole: { id: "geode-users-role", role: "users", selectable: true },
        adminRole: { id: "geode-admins-role", role: "admins", selectable: true },
        previewRole: { id: "geode-preview-role", role: "preview", selectable: false },
        developersRole: { id: "geode-developers-role", role: "developers", selectable: true },
        secretExpirationDate: new Date("2024-08-10"),
        ADGroups: ADGROUPSUPTKCH,
        protectedResources: getProtectedResources(`api://${GEODE}`, BACKENDURL.PROD),
    },
    cache: commonCacheConfig,
    system: commonSystemConfig
};

msalConfig["qual"] = {
    appFeatures: ['appConsent', 'appPMO', 'appDFHB', 'appHEADER_ShowEnv', 'appHEADER_ShowworkspaceId', 'appHEADER_ShowworkspaceId_Auto', "appHOME_ShowGraphics", 'appPORTAL_ShowGraphics'],
    auth: {
        id: 'QUAL',
        tenantId: "",
        ...getEnvHeaders('qual'),
        frontURL: FRONTURL.QUAL2,
        redirectUri: FRONTURL.QUAL2,
        postLogoutRedirectUri: FRONTURL.QUAL2,
        backendURL: BACKENDURL.QUAL2,
        checkIslocalHost: false,
        clientId: CLIENTID,
        authority: AUTHORITYONLEGACYAD,
        sites: commonSites,
        menus: commonMenus,
        workspaceIds: [{ id: "default", name: "UPTK", groups: ACL_ALL, selectable: true, selected: true }],
        userGroup: { id: "DG_PF01-TEAMS-CLOUDM365E5STD", role: "users", selectable: true },
        adminGroup: { id: "OG_480", role: "admins", selectable: true },
        previewGroup: { id: "DG_PF01-GEODE-PREVIEW", role: "preview", selectable: false },
        developersGroup: { id: "geode-developers", role: "developers", selectable: false },
        userRole: { id: "geode-users-role", role: "users", selectable: true },
        adminRole: { id: "geode-admins-role", role: "admins", selectable: true },
        previewRole: { id: "geode-preview-role", role: "preview", selectable: false },
        developersRole: { id: "geode-developers-role", role: "developers", selectable: true },
        secretExpirationDate: new Date("2024-08-10"),
        ADGroups: ADGROUPSUPTKCH,
        protectedResources: getProtectedResources(`api://${GEODE}`, BACKENDURL.QUAL2),
    },
    cache: commonCacheConfig,
    system: commonSystemConfig
};

msalConfig["local"] = {
    appFeatures: ['appPMO', 'appDFHB', 'appHEADER_ShowEnv', 'appHEADER_ShowworkspaceId', 'appHEADER_ShowworkspaceId_Auto', 'appPORTAL_ShowGraphics'],
    auth: {
        id: 'LOCAL',
        tenantId: "a1b2c3d4-7285-4d0b-8a89-8d9af09d1403", // UPT-436: DEV makers tenant (UpTK MERCHANT, all modules). Was 019c7f7c (empty Yomi DEV) which resolved to a 0-module tenant and stranded the user on onboarding. Forced at config level until backend provides full config dynamically.
        ...getEnvHeaders('local'),
        frontURL: FRONTURL.LOCAL,
        redirectUri: FRONTURL.LOCAL,
        postLogoutRedirectUri: FRONTURL.LOCAL,
        backendURL: BACKENDURL.LOCAL,
        checkIslocalHost: true,
        clientId: CLIENTID,
        authority: AUTHORITYONLEGACYAD,
        sites: commonSites,
        menus: commonMenus,
        workspaceIds: [{ id: "default", name: "UPTK", groups: ACL_ALL, selectable: true, selected: true }],
        userGroup: { id: "DG_PF01-GEODE-USER", role: "users", selectable: true },
        adminGroup: { id: "CL_IAM_LANDINGZONES_Qual", role: "admins", selectable: true },
        previewGroup: { id: "DG_PF01-GEODE-PREVIEW", role: "preview", selectable: false },
        developersGroup: { id: "geode-developers", role: "developers", selectable: false },
        userRole: { id: "geode-users-role", role: "users", selectable: true },
        adminRole: { id: "geode-admins-role", role: "admins", selectable: true },
        previewRole: { id: "geode-preview-role", role: "preview", selectable: false },
        developersRole: { id: "geode-developers-role", role: "developers", selectable: true },
        secretExpirationDate: new Date("2024-08-10"),
        ADGroups: ADGROUPSUPTKCH,
        protectedResources: getProtectedResources(`api://${GEODE}`, BACKENDURL.LOCAL),
    },
    cache: commonCacheConfig,
    system: commonSystemConfig
};

// DEV profile: deployed to Azure Static Web Apps (gray-rock-04864f903.7.azurestaticapps.net).
// backendURL is intentionally empty so all /api/* calls are relative — the SWA linked-backend
// proxy forwards them to the ACA (ca-api-geode-eun-dev). checkIslocalHost: false so
// Rest.getUrl() returns `${apiAzureUrl()}${url}` = `'' + url` = relative path.
msalConfig["dev"] = {
    appFeatures: ['appPMO', 'appDFHB', 'appHEADER_ShowEnv', 'appHEADER_ShowworkspaceId', 'appHEADER_ShowworkspaceId_Auto', 'appPORTAL_ShowGraphics'],
    auth: {
        id: 'DEV',
        tenantId: "",
        ...getEnvHeaders('dev'),
        frontURL: FRONTURL.DEV,
        redirectUri: FRONTURL.DEV,
        postLogoutRedirectUri: FRONTURL.DEV,
        backendURL: BACKENDURL.DEV,
        checkIslocalHost: false,
        clientId: CLIENTID,
        authority: AUTHORITYONLEGACYAD,
        sites: commonSites,
        menus: commonMenus,
        workspaceIds: [{ id: "default", name: "UPTK", groups: ACL_ALL, selectable: true, selected: true }],
        userGroup: { id: "DG_PF01-GEODE-USER", role: "users", selectable: true },
        adminGroup: { id: "CL_IAM_LANDINGZONES_Qual", role: "admins", selectable: true },
        previewGroup: { id: "DG_PF01-GEODE-PREVIEW", role: "preview", selectable: false },
        developersGroup: { id: "geode-developers", role: "developers", selectable: false },
        userRole: { id: "geode-users-role", role: "users", selectable: true },
        adminRole: { id: "geode-admins-role", role: "admins", selectable: true },
        previewRole: { id: "geode-preview-role", role: "preview", selectable: false },
        developersRole: { id: "geode-developers-role", role: "developers", selectable: true },
        secretExpirationDate: new Date("2024-08-10"),
        ADGroups: ADGROUPSUPTKCH,
        protectedResources: getProtectedResources(`api://${GEODE}`, BACKENDURL.DEV),
    },
    cache: commonCacheConfig,
    system: commonSystemConfig
};

export default msalConfig;

const resolveActiveProfile = () => {
    // Vite first, then common fallbacks for webpack/CRA
    const viteProfile = import.meta.env.VITE_PROFILES_ACTIVE;
    return viteProfile
        ?? process.env.VITE_PROFILES_ACTIVE
        ?? process.env.REACT_APP_PROFILES_ACTIVE
        ?? 'local';
};

const resolveWorkspaceIds = (
    config: GeodeMsalConfig,
    authContext?: AuthContextResponse | null
) => {
    if (!authContext?.tenantId) {
        return config.auth.workspaceIds;
    }

    return [
        {
            id: authContext.tenantId,
            name: authContext.tenantName || 'Tenant',
            groups: authContext.groups?.length ? authContext.groups : ACL_ALL,
            selectable: true,
            selected: true,
        },
    ];
};

const mergeConfigWithAuthContext = (
    config: GeodeMsalConfig,
    authContext?: AuthContextResponse | null
): GeodeMsalConfig => {
    if (!authContext) {
        return config;
    }

    return {
        ...config,
        appFeatures: authContext.features?.length ? authContext.features : config.appFeatures,
        auth: {
            ...config.auth,
            tenantId: authContext.tenantId || config.auth.tenantId,
            workspaceIds: resolveWorkspaceIds(config, authContext),
            menus: authContext.menus
                ? {
                    users: authContext.menus.users ?? config.auth.menus.users,
                    admins: authContext.menus.admins ?? config.auth.menus.admins,
                }
                : config.auth.menus,
            sites: (authContext.sites as Record<string, AppSite> | undefined) ?? config.auth.sites,
        },
    };
};

export const getActiveConfig = (): GeodeMsalConfig => {
    const profile = resolveActiveProfile() as string;
    const authContext = getAuthContextState();

    if (!profile) {
        console.warn('VITE_PROFILES_ACTIVE is not set; falling back to local config.');
        return mergeConfigWithAuthContext(msalConfig['local'], authContext);
    }

    const config = msalConfig[profile];
    if (!config) {
        console.warn(`No configuration found for profile: ${profile}. Falling back to local config.`);
        return mergeConfigWithAuthContext(msalConfig['local'], authContext);
    }

    return mergeConfigWithAuthContext(config, authContext);
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
